import { SignedProof } from '../../types';
import { ProofVerificationStrategy } from '.';
import logger from '../logger';
import { verify } from '@civic/prove-icp-principal';

/**
 * Returns whether the given keyToCheck was used to sign the given signedProof.
 * @param signedProof The proof to evaluate
 * @param keyToCheckHex The Solana key in hex format
 * @param expectedMessage The message we expect to appear in the signature. This is usually a nonce.
 * @returns Boolean, whether the given key was a signer of the proof.
 */
export const verifyICPProof: ProofVerificationStrategy = async (
  signedProof: SignedProof,
  icpPrincipal: string,
  expectedMessage?: string
): Promise<boolean> => {
  try {
    // Type guard to ensure we are dealing with ICPWalletSignedProofOptions
    const options = signedProof.options;
    if (
      'currentTimeNsOverride' in options ||
      'iiCanisterIdOverride' in options ||
      'rootPublicKeyOverride' in options
    ) {
      // This performs an ecrecover
      return await verify(
        icpPrincipal,
        signedProof.proof,
        {
          message: expectedMessage,
        },
        options.currentTimeNsOverride
          ? BigInt(options.currentTimeNsOverride)
          : undefined,
        options.iiCanisterIdOverride ?? undefined,
        options.rootPublicKeyOverride
          ? new Uint8Array(Buffer.from(options.rootPublicKeyOverride, 'hex'))
          : undefined
      );
    } else {
      throw new Error('Invalid options type for ICP verification.');
    }
  } catch (err) {
    logger.warn(
      `Verification failed for principal ${icpPrincipal}. Skipping.`,
      err
    );
    return false;
  }
};

const isPrincipal = (addressOrKey: string): boolean => {
  // TODO check if ICP lib already has this?
  const principalPattern =
    /^[a-z2-7]{5}-[a-z2-7]{5}-[a-z2-7]{5}-[a-z2-7]{5}-[a-z2-7]{5}-[a-z2-7]{5}-[a-z2-7]{5}-[a-z2-7]{5}-[a-z2-7]{5}-[a-z2-7]{5}-[a-z2-7]{3}$/;
  return principalPattern.test(addressOrKey);
};

const icpKeyToPrincipal = (addressOrKey: string): string => {
  // Make sure any DID prefixes are stripped off.
  addressOrKey = addressOrKey.replace(/(.*):/g, '');

  if (!isPrincipal(addressOrKey)) {
    throw new Error(`${addressOrKey} is not a principal!`);
  }

  return addressOrKey;
};

export const icpKeyMatchesWallet = (keyHex: string, walletAddress: string) => {
  try {
    if (!isPrincipal(walletAddress)) {
      return false;
    }
    return (
      icpKeyToPrincipal(keyHex).toLowerCase() === walletAddress.toLowerCase()
    );
  } catch {
    return false;
  }
};
